/**
 * 반 겹침 점검 공용 엔진: 학생 글끼리 '같은 문장'과 '거의 같은 문장'을 문장 단위로 찾는다.
 *
 * 기준(외부 근거):
 * - 같은 문장: 띄어쓰기·문장부호를 빼면 글자가 모두 같은 문장 (감사원 생기부 감사 '모든 문장 동일').
 * - 거의 같은 문장: 어절 6개 이상이 이어서 같거나, 어절 1~2개만 다른 문장
 *   (교육부 2008 표절 기준 '6어절 연속', 경성고 감사 '한두 단어만 다른 문장').
 * - 몇 명이 같은지로 무게를 나눈다: 2명↑ 겹침, 10명↑ 교육청 감사에서 지적된 규모,
 *   행동특성 반 70%↑ 감사원이 지적한 규모.
 *
 * 브라우저에서 바로 계산하므로 무료·즉시이고, AI는 걸린 문장이 공통 활동 설명인지 판단하는 데만 쓴다.
 * import 없이 이 파일 하나로 동작해야 한다(운영 데이터 검증 스크립트가 Node로 그대로 불러 쓴다).
 */

export const NEAR_MIN_WORDS = 6;
export const MIN_SENTENCE_CHARS = 12;
export const MIN_EDIT_WORDS = 5;
export const AUDIT_PEOPLE = 10;
export const NATIONAL_SHARE = 0.7;
export const NATIONAL_MIN_STUDENTS = 5;
export const SOURCE_MIN_CHARS = 20;
// 어절 6개 이어짐만으로는 긴 문장의 일부 표현이 같을 때도 걸린다. 짧은 쪽 문장의 40% 이상이 이어서 같거나
// 7어절 이상 이어서 같을 때만 '거의 같은 문장'으로 본다. 운영 9,703개 반에서 뽑은 2,000쌍을 AI로 판정했을 때
// 복사로 볼 만한 비율이 87.8% → 90.4%가 되고(오탐 29% 감소) 실제 복사는 92%를 그대로 잡는다.
// 6어절·40% 미만 묶음만 복사 비율이 68%로 낮았고, 기준을 더 올리면 실제 복사를 21% 이상 놓친다.
export const NEAR_MIN_RATIO = 0.4;
export const STRONG_RUN = 7;

const NON_WORD = /[^0-9A-Za-z가-힣]/g;

export const sentenceKey = (text) => String(text || '').replace(NON_WORD, '');

export const sentenceWords = (text) => String(text || '')
  .split(/\s+/)
  .map((word) => word.replace(NON_WORD, ''))
  .filter(Boolean);

/** 문장과 원문 위치. 소수점(3.5)에서는 자르지 않는다. */
export const splitSentences = (text) => {
  const body = String(text || '');
  const out = [];
  const push = (start, end) => {
    const raw = body.slice(start, end);
    const lead = raw.length - raw.trimStart().length;
    const trimmed = raw.trim();
    if (trimmed) out.push({ text: trimmed, start: start + lead, end: start + lead + trimmed.length });
  };
  let start = 0;
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i];
    if (ch === '\n') {
      push(start, i);
      start = i + 1;
    } else if ('.!?。'.includes(ch)) {
      const decimal = ch === '.' && /\d/.test(body[i - 1] || '') && /\d/.test(body[i + 1] || '');
      if (!decimal) {
        push(start, i + 1);
        start = i + 1;
      }
    }
  }
  push(start, body.length);
  return out;
};

const wordGrams = (words, n) => {
  const grams = new Set();
  for (let i = 0; i + n <= words.length; i += 1) grams.add(words.slice(i, i + n).join(' '));
  return grams;
};

const longestRun = (a, b) => {
  let best = 0;
  let prev = new Array(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = new Array(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j += 1) {
      if (a[i - 1] === b[j - 1]) {
        cur[j] = prev[j - 1] + 1;
        if (cur[j] > best) best = cur[j];
      }
    }
    prev = cur;
  }
  return best;
};

const wordEditDistance = (a, b, limit) => {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = [i];
    let rowMin = cur[0];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > limit) return limit + 1;
    prev = cur;
  }
  return prev[b.length];
};

/** 어절 수에 따라 허용하는 '다른 어절' 수: 5~7어절은 1개, 8어절 이상은 2개. */
export const allowedWordEdits = (wordCount) => (wordCount >= 8 ? 2 : wordCount >= MIN_EDIT_WORDS ? 1 : 0);

/** 두 문장이 어떤 단계로 겹치는지: 'same' | 'near' | null */
export const compareSentences = (a, b, { minRatio = NEAR_MIN_RATIO, strongRun = STRONG_RUN } = {}) => {
  if (a.key.length < MIN_SENTENCE_CHARS || b.key.length < MIN_SENTENCE_CHARS) return null;
  if (a.key === b.key) return 'same';
  if (a.words.length >= NEAR_MIN_WORDS && b.words.length >= NEAR_MIN_WORDS) {
    let shared = false;
    for (const gram of a.grams) if (b.grams.has(gram)) { shared = true; break; }
    if (shared) {
      if (!minRatio) return 'near';
      const run = longestRun(a.words, b.words);
      const shorter = Math.min(a.words.length, b.words.length);
      if (run / shorter >= minRatio || (strongRun && run >= strongRun)) return 'near';
    }
  }
  const limit = Math.min(allowedWordEdits(a.words.length), allowedWordEdits(b.words.length));
  if (limit > 0 && wordEditDistance(a.words, b.words, limit) <= limit) return 'near';
  return null;
};

const buildSourceIndex = (contextText) => {
  const key = sentenceKey(contextText);
  const grams = wordGrams(sentenceWords(contextText), NEAR_MIN_WORDS);
  return { key, grams };
};

const isFromSource = (sentence, source) => {
  if (!source || (!source.key && source.grams.size === 0)) return false;
  if (sentence.key.length >= SOURCE_MIN_CHARS && source.key.includes(sentence.key)) return true;
  for (const gram of sentence.grams) if (source.grams.has(gram)) return true;
  return false;
};

export const severityFor = (people, studentCount, { category = '' } = {}) => {
  if (category === '행동발달' && studentCount >= NATIONAL_MIN_STUDENTS && people / studentCount > NATIONAL_SHARE) {
    return 'national';
  }
  if (people >= AUDIT_PEOPLE) return 'audit';
  if (people >= 2) return 'overlap';
  return 'none';
};

const SEVERITY_RANK = { none: 0, overlap: 1, audit: 2, national: 3 };

/**
 * texts: 학생별 글. contextText: 평가계획서·공통 활동 자료처럼 모든 학생이 함께 받은 자료(있으면).
 * 반환:
 * - students[i].sentences: 겹치는 문장만 {text,start,end,tier,people,others,clusterId,fromSource}
 * - groups: 겹치는 문장 묶음 {id,tier,people,students,sample,fromSource,severity}
 * - summary: {studentCount, flaggedStudents, sameGroups, nearGroups, maxPeople, severity}
 */
export const analyzeSentenceOverlap = (texts = [], {
  contextText = '',
  category = '',
  minRatio = NEAR_MIN_RATIO,
  strongRun = STRONG_RUN,
} = {}) => {
  const list = (Array.isArray(texts) ? texts : []).map((text) => String(text || ''));
  const source = contextText ? buildSourceIndex(contextText) : null;
  const nodes = [];
  list.forEach((text, studentIndex) => {
    splitSentences(text).forEach((piece) => {
      const words = sentenceWords(piece.text);
      nodes.push({
        id: nodes.length,
        student: studentIndex,
        ...piece,
        key: sentenceKey(piece.text),
        words,
        grams: wordGrams(words, NEAR_MIN_WORDS),
      });
    });
  });

  const candidates = new Map();
  const index = new Map();
  nodes.forEach((node) => {
    if (node.key.length < MIN_SENTENCE_CHARS) return;
    const keys = new Set([`k:${node.key}`]);
    node.grams.forEach((gram) => keys.add(`g:${gram}`));
    if (node.words.length >= MIN_EDIT_WORDS) {
      for (let i = 0; i + 2 <= node.words.length; i += 1) keys.add(`b:${node.words[i]} ${node.words[i + 1]}`);
    }
    keys.forEach((key) => {
      const bucket = index.get(key);
      if (bucket) {
        bucket.forEach((other) => {
          if (nodes[other].student === node.student) return;
          const pair = other < node.id ? `${other}:${node.id}` : `${node.id}:${other}`;
          candidates.set(pair, true);
        });
        bucket.push(node.id);
      } else {
        index.set(key, [node.id]);
      }
    });
  });

  const parent = nodes.map((node) => node.id);
  const find = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const links = nodes.map(() => new Map());
  candidates.forEach((_, pair) => {
    const [a, b] = pair.split(':').map(Number);
    const tier = compareSentences(nodes[a], nodes[b], { minRatio, strongRun });
    if (!tier) return;
    const prevA = links[a].get(nodes[b].student);
    if (prevA !== 'same') links[a].set(nodes[b].student, tier);
    const prevB = links[b].get(nodes[a].student);
    if (prevB !== 'same') links[b].set(nodes[a].student, tier);
    parent[find(a)] = find(b);
  });

  const studentCount = list.length;
  const clusters = new Map();
  nodes.forEach((node) => {
    if (links[node.id].size === 0) return;
    const root = find(node.id);
    if (!clusters.has(root)) clusters.set(root, []);
    clusters.get(root).push(node);
  });

  const groups = [];
  const students = list.map((_, i) => ({ index: i, sentences: [], maxPeople: 0, severity: 'none' }));
  [...clusters.values()]
    .map((members) => members.sort((x, y) => x.student - y.student || x.start - y.start))
    .sort((x, y) => x[0].student - y[0].student || x[0].start - y[0].start)
    .forEach((members) => {
      const id = groups.length;
      const memberStudents = [...new Set(members.map((m) => m.student))];
      const allSame = members.every((m) => m.key === members[0].key);
      const people = Math.max(...members.map((m) => links[m.id].size + 1));
      const fromSource = members.some((m) => isFromSource(m, source));
      const severity = severityFor(people, studentCount, { category });
      groups.push({
        id,
        tier: allSame ? 'same' : 'near',
        people,
        students: memberStudents,
        sample: members[0].text,
        fromSource,
        severity,
      });
      members.forEach((m) => {
        const mPeople = links[m.id].size + 1;
        const tier = [...links[m.id].values()].includes('same') ? 'same' : 'near';
        const entry = students[m.student];
        entry.sentences.push({
          text: m.text,
          start: m.start,
          end: m.end,
          tier,
          people: mPeople,
          others: [...links[m.id].keys()].sort((x, y) => x - y),
          clusterId: id,
          fromSource,
        });
        entry.maxPeople = Math.max(entry.maxPeople, mPeople);
        const sSeverity = severityFor(mPeople, studentCount, { category });
        if (SEVERITY_RANK[sSeverity] > SEVERITY_RANK[entry.severity]) entry.severity = sSeverity;
      });
    });

  students.forEach((entry) => entry.sentences.sort((x, y) => x.start - y.start));
  const flagged = students.filter((s) => s.sentences.length > 0);
  const maxPeople = groups.reduce((acc, g) => Math.max(acc, g.people), 0);
  const severity = groups.reduce(
    (acc, g) => (SEVERITY_RANK[g.severity] > SEVERITY_RANK[acc] ? g.severity : acc),
    'none',
  );
  return {
    students,
    groups,
    summary: {
      studentCount,
      flaggedStudents: flagged.length,
      sameGroups: groups.filter((g) => g.tier === 'same').length,
      nearGroups: groups.filter((g) => g.tier === 'near').length,
      sourceGroups: groups.filter((g) => g.fromSource).length,
      maxPeople,
      severity,
    },
  };
};
